import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  ListGroup,
  ListGroupItem,
} from 'reactstrap';
import {
  startCase,
  uniqBy,
} from 'lodash';
import {
  useFetch,
  PseudoStates,
  CardHeader,
} from '@cordata/buffet';
import { format } from 'date-fns';

import usePatient from '~/utils/patient/hook';
import BigNumberCard from '~/components/common/BigNumberCard';

const PatientFaceSheetNavigator = () => {
  const [, patient] = usePatient();
  const [addressStatus, address] = useFetch(`patients/${patient.id}/primary-address`);
  const [countAll, setCountAll] = useState(0);
  const [countWeaponInvolved, setCountWeaponInvolved] = useState(0);
  const [countEval, setCountEval] = useState(0);

  const [citDetailsStatus, citDetails] = useFetch('data/public/nav-list-view-medical-event-detail', {
    params: {
      'patient-uid': patient.id,
      'medical-event-name': 'cit',
      'all-data': 1,
    },
    instance: 'p',
  });

  const [outboundReferralsStatus, outboundReferrals] = useFetch('data/public/nav-list-view-recent-outbound-ref', {
    params: {
      'patient-uid': patient.id,
      sort: 'event-date',
    },
    instance: 'p',
  });

  const [citReasonsStatus, citReasons] = useFetch('data/public/nav-list-view-recent-cit-reason', {
    params: {
      'patient-uid': patient.id,
      distinct: 'value',
    },
    instance: 'p',
  });

  useEffect(() => {
    if (citDetailsStatus === 'success') {
      if (citDetails.length > 0) {
        setCountAll(uniqBy(citDetails, 'attributes.careEventUid').length);
        setCountWeaponInvolved(uniqBy(citDetails.filter(d => {
          /* eslint-disable-next-line */
          return d.attributes.name === 'weapon_involved' && d.attributes.value && d.attributes.value.replace(/[\[\]]/g, '')
        }), 'attributes.careEventUid').length);
        setCountEval(uniqBy(citDetails.filter(d => d.attributes.name === 'subject_taken_into_custody_for_evaluation' && d.attributes.value?.match(/yes|true/i)), 'attributes.careEventUid').length);
      } else {
        setCountAll(0);
        setCountWeaponInvolved(0);
        setCountEval(0);
      }
    }
  }, [citDetailsStatus]);

  return (
    <>
      <Row className="my-3">
        <Col>
          <span className="font-weight-bold">Primary Address:</span>{' '}

          {addressStatus === 'success' ? <>
            {address.attributes?.addr1}{address.attributes?.addr2 && ` ${address.attributes?.addr2}`} {address.attributes?.city}, {address.attributes?.provinceIso} {address.attributes?.postCode}
          </> : <>
            {startCase(addressStatus)}
          </>}
        </Col>
      </Row>

      <Row>
        <Col>
          <BigNumberCard
            title="CIT Events"
            number={
              citDetailsStatus === 'success'
                ? countAll
                : <PseudoStates status={citDetailsStatus} />
            }
          />
        </Col>

        <Col>
          <BigNumberCard
            title="CIT Events Involving Weapons"
            number={
              citDetailsStatus === 'success'
                ? countWeaponInvolved
                : <PseudoStates status={citDetailsStatus} />
            }
          />
        </Col>

        <Col>
          <BigNumberCard
            title="Emergency Hospitalizations"
            number={
              citDetailsStatus === 'success'
                ? countEval
                : <PseudoStates status={citDetailsStatus} />
            }
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col>
          <Card>
            <CardHeader color="primary" inverse>
              Top Reasons For CIT Events
            </CardHeader>

            <CardBody className="p-0">
              {citReasonsStatus === 'success' ? (
                <ListGroup flush>
                  {citReasons.map((r, i) => (
                    <ListGroupItem>
                      {i + 1}. {r.attributes.value}
                    </ListGroupItem>
                  ))}

                  {citReasons.length === 0 && (
                    <ListGroupItem>
                      <span className="text-tertiary">No items.</span>
                    </ListGroupItem>
                  )}
                </ListGroup>
              ) : (
                <PseudoStates status={citReasonsStatus} />
              )}
            </CardBody>
          </Card>
        </Col>

        <Col>
          <Card>
            <CardHeader color="primary" inverse>
              Top Mental Health Referrals
            </CardHeader>

            <CardBody className="p-0">
              {outboundReferralsStatus === 'success' ? (
                <ListGroup flush>
                  {outboundReferrals.map((r) => (
                    <ListGroupItem
                      action
                      tag={Link}
                      to={`/patients/${patient.id}/outbound-referrals/${r.id}`}
                      className="text-primary"
                    >
                      {r.attributes.eventDate ? format(r.attributes.eventDate, 'MM/DD/YYYY') : 'Unscheduled'} @ {r.attributes.facilityName}
                    </ListGroupItem>
                  ))}

                  {outboundReferrals.length === 0 && (
                    <ListGroupItem>
                      <span className="text-tertiary">No items.</span>
                    </ListGroupItem>
                  )}
                </ListGroup>
              ) : (
                <PseudoStates status={outboundReferralsStatus} />
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PatientFaceSheetNavigator;
